import { Mail } from 'lucide-react'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { apiFieldMessages } from '@/lib/contacts/errors'

interface EmailBrandingCardProps {
  senderName: string
  replyToAddress: string
  logoUrl: string
  accentColor: string
  postalAddress: string
  setupTask: string | null
  saved: boolean
  pending: boolean
  error: Error | null
  onSenderNameChange: (value: string) => void
  onReplyToChange: (value: string) => void
  onLogoUrlChange: (value: string) => void
  onAccentColorChange: (value: string) => void
  onPostalAddressChange: (value: string) => void
  onSubmit: () => void
  readOnly?: boolean
}

export function EmailBrandingCard({
  senderName,
  replyToAddress,
  logoUrl,
  accentColor,
  postalAddress,
  setupTask,
  saved,
  pending,
  error,
  onSenderNameChange,
  onReplyToChange,
  onLogoUrlChange,
  onAccentColorChange,
  onPostalAddressChange,
  onSubmit,
  readOnly = false,
}: EmailBrandingCardProps) {
  return (
    <section className="p-4">
      <h2 className="flex items-center gap-2 pb-3 text-body font-semibold">
        <Mail className="h-4 w-4 text-primary" strokeWidth={1.5} />
        Branding and sender
      </h2>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <FormField label="Sender name" htmlFor="system-email-sender-name">
          <Input
            className="h-9"
            id="system-email-sender-name"
            value={senderName}
            disabled={readOnly}
            onChange={(event) => onSenderNameChange(event.target.value)}
          />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Logo URL"
            htmlFor="system-email-logo-url"
            error={apiFieldMessages(error, 'system_email_logo_url')[0]}
          >
            <Input
              className="h-9"
              id="system-email-logo-url"
              inputMode="url"
              value={logoUrl}
              disabled={readOnly}
              onChange={(event) => onLogoUrlChange(event.target.value)}
            />
          </FormField>
          <FormField
            label="Accent color"
            htmlFor="system-email-accent-color"
            hint="6-digit hex · #3157A4"
            error={apiFieldMessages(error, 'system_email_accent_color')[0]}
          >
            <Input
              className="h-9"
              id="system-email-accent-color"
              value={accentColor}
              disabled={readOnly}
              onChange={(event) => onAccentColorChange(event.target.value)}
            />
          </FormField>
        </div>
        <FormField label="Postal mailing address" htmlFor="system-email-postal-address">
          <Textarea
            className="min-h-20"
            id="system-email-postal-address"
            value={postalAddress}
            disabled={readOnly}
            onChange={(event) => onPostalAddressChange(event.target.value)}
          />
        </FormField>
        {setupTask && <Alert variant="warning">{setupTask}</Alert>}
        <FormField
          label="Reply-To address"
          htmlFor="system-email-reply-to-address"
          error={apiFieldMessages(error, 'system_email_reply_to_address')[0]}
        >
          <Input
            className="h-9"
            id="system-email-reply-to-address"
            inputMode="email"
            value={replyToAddress}
            disabled={readOnly}
            onChange={(event) => onReplyToChange(event.target.value)}
          />
        </FormField>
        {saved && <p className="text-body-sm text-foreground">Email branding saved.</p>}
        {!readOnly && (
          <Button type="submit" size="sm" disabled={pending}>
            {pending ? 'Saving...' : 'Save email branding'}
          </Button>
        )}
      </form>
    </section>
  )
}
